import { useDay } from '@datepicker-react/hooks';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useId, useMemo, type FC, type ReactNode } from 'react';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useNow } from 'src/hooks/use-now';
import { cn } from 'src/utils/cn';
import { timeZone } from 'src/utils/date-time-format';

type DstChange = 'REGULAR' | 'CET_TO_CEST' | 'CEST_TO_CET';

type DayProps = {
  dayLabel: string;
  date: Date;
  startDate: Date | null;
  endDate: Date | null;
  /**
   * Either a hovered date or dragged over date.
   */
  targetDate: Date | null;
  isDayDragging: IsDayDragging;
  onDateSelect: (date: Date) => void;
  onDayHover: (date: Date | null) => void;
  isDateBlocked: (date: Date) => boolean;
  dstChange: DstChange;
};

export const Day: FC<DayProps> = ({
  dayLabel,
  date,
  startDate,
  endDate,
  targetDate,
  isDayDragging,
  onDateSelect,
  onDayHover,
  isDateBlocked,
  dstChange,
}) => {
  const { onClick, disabledDate } = useDay({
    date,
    focusedDate: null,
    isDateFocused: () => false,
    isDateSelected: () => false,
    isDateHovered: () => false,
    isDateBlocked,
    isFirstOrLastSelectedDate: () => false,
    onDateFocus: () => {},
    onDateHover: () => {},
    onDateSelect,
  });

  const { isStartDate, isEndDate, isToday, mode } = useDayMode(
    date,
    startDate,
    endDate,
    targetDate,
    isDayDragging
  );

  const isThisDateBlocked = isDateBlocked(date);
  const isStartOrEndDate = isStartDate || isEndDate;
  const isFullRange = Boolean(startDate && endDate);
  // we don't use `disabled` of `useDraggable`, because the elements might still be interactive
  // (e.g. clickable), therefore we manually manage the draggable props
  const isDraggable = isStartOrEndDate && isFullRange && !isThisDateBlocked;

  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id: useId(),
    data: { date } satisfies DayDroppableData,
    disabled: isThisDateBlocked,
  });

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableNodeRef,
    isDragging: isThisDayDragging,
  } = useDraggable({
    id: useId(),
    data: { isStartDate } satisfies DayDraggableData,
  });

  return (
    <Tooltip content={dstTooltipMap[dstChange]}>
      {(targetProps) => (
        <button
          {...targetProps}
          className={cn(
            `grid h-[3rem] w-full cursor-pointer border-none bg-none p-0`,
            disabledDate && 'cursor-not-allowed',
            isDraggable && 'cursor-grab',
            !isDayDragging &&
              !disabledDate &&
              `
                [&:hover_[data-mode="default"]]:rounded-xl
                [&:hover_[data-mode="default"]]:bg-background-surface-hover
                [&:hover_[data-mode="default"]]:legacy:bg-legacy-brand-light-4
              `
          )}
          ref={(node) => {
            setDraggableNodeRef(node);
            setDroppableNodeRef(node);
          }}
          type="button"
          data-testid={`day-${date.toISOString()}`}
          onClick={onClick}
          onMouseEnter={() => onDayHover(date)}
          onMouseLeave={() => onDayHover(null)}
          disabled={disabledDate}
          {...(isDraggable && attributes)}
          {...(isDraggable && listeners)}
        >
          <div
            className={cn(
              `
                relative content-center border-2 border-dashed
                border-transparent text-foreground-default
                legacy:text-legacy-brand
              `,
              'h-full', // needed for older safari versions
              isToday &&
                `
                  text-foreground-support-highlight
                  legacy:text-legacy-brand-secondary
                `,
              dayModeStyles[mode],
              isThisDayDragging &&
                `
                  border-2 border-dashed border-border-surface-subdued
                  legacy:border-legacy-brand-light-4
                `,
              disabledDate &&
                `
                  text-foreground-surface-disabled
                  legacy:text-legacy-inactive
                `,
              disabledDate &&
                (mode === 'single' || mode === 'start' || mode === 'end') &&
                `
                  bg-background-surface-disabled
                  text-foreground-surface-disabled
                  legacy:bg-legacy-inactive legacy:text-white
                `
            )}
            data-mode={mode} // used as a selector for hover styles
          >
            {dayLabel}
            {dstAppendixMap[dstChange] ??
              (isToday ? (
                <span className={dayAppendixClasses} aria-hidden>
                  ●
                </span>
              ) : null)}
          </div>
        </button>
      )}
    </Tooltip>
  );
};

type DayMode =
  | 'default'
  | 'target-single'
  | 'target-start'
  | 'target-end'
  | 'target-inner'
  | 'single'
  | 'start'
  | 'end'
  | 'inner'
  | 'unselect';

const targetInnerClasses = cn(`
  border-2 border-dashed border-border-surface-subdued
  bg-background-surface-minimal
  legacy:border-legacy-brand-light-4 legacy:bg-legacy-brand-light-5
`);

const dayModeStyles: Record<DayMode, string> = {
  default: '',
  'target-single': cn(targetInnerClasses, 'rounded-xl'),
  'target-start': cn(targetInnerClasses, 'rounded-l-xl'),
  'target-end': cn(targetInnerClasses, 'rounded-r-xl'),
  'target-inner': targetInnerClasses,
  single: cn(`
    rounded-xl bg-background-surface-selected text-foreground-surface-selected
    legacy:bg-legacy-brand-secondary legacy:text-white
  `),
  start: cn(`
    rounded-l-xl bg-background-surface-selected text-foreground-surface-selected
    legacy:bg-legacy-brand-secondary legacy:text-white
  `),
  end: cn(`
    rounded-r-xl bg-background-surface-selected text-foreground-surface-selected
    legacy:bg-legacy-brand-secondary legacy:text-white
  `),
  inner: cn(`
    bg-background-surface-hover
    legacy:bg-legacy-brand-light-4
  `),
  unselect: cn(`
    bg-background-surface-minimal
    legacy:bg-legacy-brand-light-5
  `),
};

const dayAppendixClasses = cn(`
  absolute bottom-xs left-0 w-full text-[0.8rem] leading-0
`);

const dstAppendixMap: Record<DstChange, ReactNode> = {
  REGULAR: null,
  CET_TO_CEST: <span className={dayAppendixClasses}>CEST</span>,
  CEST_TO_CET: <span className={dayAppendixClasses}>CET</span>,
};

const dstTooltipMap: Record<DstChange, ReactNode> = {
  REGULAR: null,
  CET_TO_CEST: 'Start of CEST, clocks move forward 1 hour.',
  CEST_TO_CET: 'Start of CET, clocks move back 1 hour.',
};

export type IsDayDragging = 'start' | 'end' | 'single' | null;

export type DayDroppableData = {
  date: Date;
};

export type DayDraggableData = {
  isStartDate: boolean;
};

function useDayMode(
  currentDate: Date,
  startDate: Date | null,
  endDate: Date | null,
  targetDate: Date | null,
  isDayDragging: IsDayDragging
) {
  const now = useNow();

  return useMemo(() => {
    const nowBerlin = toZonedTime(now, timeZone);
    const currentDateBerlin = toZonedTime(currentDate, timeZone);
    const startDateBerlin = startDate && toZonedTime(startDate, timeZone);
    const endDateBerlin = endDate && toZonedTime(endDate, timeZone);
    const targetDateBerlin = targetDate && toZonedTime(targetDate, timeZone);

    const isToday = isSameDay(currentDateBerlin, nowBerlin);
    const isStartDate = startDateBerlin
      ? isSameDay(startDateBerlin, currentDateBerlin)
      : false;
    const isEndDate = endDateBerlin
      ? isSameDay(endDateBerlin, currentDateBerlin)
      : false;

    let mode: DayMode = 'default';

    if (
      startDateBerlin &&
      endDateBerlin &&
      isAfterDay(currentDateBerlin, startDateBerlin) &&
      isBeforeDay(currentDateBerlin, endDateBerlin)
    ) {
      mode = 'inner';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      endDateBerlin &&
      ((isDayDragging === 'start' &&
        isAfterDay(currentDateBerlin, startDateBerlin) &&
        isBeforeDay(currentDateBerlin, targetDateBerlin)) ||
        (isDayDragging === 'end' &&
          isAfterDay(currentDateBerlin, targetDateBerlin) &&
          isBeforeDay(currentDateBerlin, endDateBerlin)))
    ) {
      mode = 'unselect';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      endDateBerlin &&
      isSameDay(currentDateBerlin, targetDateBerlin) &&
      ((isDayDragging === 'start' &&
        isBefore(targetDateBerlin, endDateBerlin)) ||
        (isDayDragging === 'end' &&
          isBefore(targetDateBerlin, startDateBerlin)))
    ) {
      // dragging over case
      mode = 'target-start';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      !endDateBerlin &&
      isBeforeDay(currentDateBerlin, startDateBerlin) &&
      isSameDay(currentDateBerlin, targetDateBerlin)
    ) {
      // hover preview case
      mode = 'target-start';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      endDateBerlin &&
      isSameDay(currentDateBerlin, targetDateBerlin) &&
      ((isDayDragging === 'start' &&
        isBefore(endDateBerlin, targetDateBerlin)) ||
        (isDayDragging === 'end' &&
          isBefore(startDateBerlin, targetDateBerlin)))
    ) {
      // dragging over case
      mode = 'target-end';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      !endDateBerlin &&
      isAfterDay(currentDateBerlin, startDateBerlin) &&
      isSameDay(currentDateBerlin, targetDateBerlin)
    ) {
      // hover preview case
      mode = 'target-end';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      endDateBerlin &&
      ((isDayDragging === 'start' &&
        isBeforeDay(currentDateBerlin, startDateBerlin) &&
        isAfterDay(currentDateBerlin, targetDateBerlin)) ||
        (isDayDragging === 'start' &&
          isBeforeDay(currentDateBerlin, targetDateBerlin) &&
          isAfterDay(currentDateBerlin, endDateBerlin)) ||
        (isDayDragging === 'end' &&
          isAfterDay(currentDateBerlin, endDateBerlin) &&
          isBeforeDay(currentDateBerlin, targetDateBerlin)) ||
        (isDayDragging === 'end' &&
          isAfterDay(currentDateBerlin, targetDateBerlin) &&
          isBeforeDay(currentDateBerlin, startDateBerlin)))
    ) {
      // dragging over case
      mode = 'target-inner';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      !endDateBerlin &&
      ((isBeforeDay(currentDateBerlin, startDateBerlin) &&
        isAfterDay(currentDateBerlin, targetDateBerlin)) ||
        (isAfterDay(currentDateBerlin, startDateBerlin) &&
          isBeforeDay(currentDateBerlin, targetDateBerlin)))
    ) {
      // hover preview case
      mode = 'target-inner';
    }

    if (
      targetDateBerlin &&
      isDayDragging === 'single' &&
      isSameDay(currentDateBerlin, targetDateBerlin)
    ) {
      mode = 'target-single';
    }

    if (isStartDate) {
      mode = 'start';
      if (!endDateBerlin) {
        if (targetDateBerlin) {
          if (isBeforeDay(targetDateBerlin, startDateBerlin!)) {
            mode = 'end';
          } else if (isAfterDay(targetDateBerlin, startDateBerlin!)) {
            mode = 'start';
          } else {
            mode = 'single';
          }
        } else mode = 'single';
      }
    }

    if (isEndDate) {
      mode = 'end';
      if (isStartDate) mode = 'single';
    }

    return { isToday, isStartDate, isEndDate, mode };
  }, [currentDate, startDate, endDate, targetDate, isDayDragging, now]);
}

// Ignore time for the isBefore comparison.
function isBeforeDay(date: Date, dateToCompare: Date) {
  const immutableDate = new Date(date.getTime());
  const immutableDateToCompare = new Date(dateToCompare.getTime());

  immutableDate.setHours(0, 0, 0, 0);
  immutableDateToCompare.setHours(0, 0, 0, 0);

  return isBefore(immutableDate, immutableDateToCompare);
}

// Ignore time for the isAfter comparison.
function isAfterDay(date: Date, dateToCompare: Date) {
  const immutableDate = new Date(date.getTime());
  const immutableDateToCompare = new Date(dateToCompare.getTime());

  immutableDate.setHours(0, 0, 0, 0);
  immutableDateToCompare.setHours(0, 0, 0, 0);

  return isAfter(immutableDate, immutableDateToCompare);
}
